/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * src/types/landingPageConfig.ts
 * 
 * Tipagem e valores padrão da configuração centralizada da Landing Page Institucional.
 * Suporta Bloco 2A (Institucional), Bloco 2B (Hero + Públicos), Bloco 2C (Quem Somos + Objetivo + Módulos),
 * Bloco 2D (Modelos Comerciais + Benefícios) e Bloco 2E (Contato, CTA Final e Rodapé).
 */

export interface InstitutionalConfig {
  brandName: string;
  officialEmail: string;
  cnpj: string;
}

export interface HeroConfig {
  eyebrow: string;
  title: string;
  subtitle: string;
  primaryCtaLabel: string;
  arenaCtaLabel: string;
  capabilityTitle: string;
  capabilityDescription: string;
}

export interface AudienceItemConfig {
  title: string;
  description: string;
  ctaLabel: string;
}

export interface AudiencesConfig {
  athlete: AudienceItemConfig;
  arenaOwner: AudienceItemConfig;
}

export interface AboutConfig {
  title: string;
  description: string;
}

export interface ObjectiveConfig {
  title: string;
  description: string;
}

export interface ModuleItemConfig {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
}

export interface CommercialModelItemConfig {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  enabled: boolean;
}

export interface CommercialModelsConfig {
  title: string;
  description: string;
  models: CommercialModelItemConfig[];
}

export interface BenefitItemConfig {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
}

export interface BenefitsConfig {
  title: string;
  description: string;
  items: BenefitItemConfig[];
}

export interface ContactConfig {
  title: string;
  description: string;
}

export interface FinalCtaConfig {
  title: string;
  subtitle: string;
}

export interface FooterConfig {
  description: string;
  copyright: string;
}

export interface LandingPageConfig {
  institutional: InstitutionalConfig;
  hero: HeroConfig;
  audiences: AudiencesConfig;
  about: AboutConfig;
  objective: ObjectiveConfig;
  modules: ModuleItemConfig[];
  commercialModels: CommercialModelsConfig;
  benefits: BenefitsConfig;
  contact: ContactConfig;
  finalCta: FinalCtaConfig;
  footer: FooterConfig;
  updatedAt?: string;
  updatedBy?: string;
}

export const DEFAULT_LANDING_PAGE_CONFIG: LandingPageConfig = {
  institutional: {
    brandName: "Hub Esportivo",
    officialEmail: "contato@hubesportivo.net.br",
    cnpj: "68.421.842/0001-50",
  },
  hero: {
    eyebrow: "Tecnologia para Arenas & Espaços Esportivos",
    title: "Gestão completa para arenas, clubes e espaços esportivos.",
    subtitle: "Centralize quadras, reservas, horários, aulas, treinos, torneios, vendas, PDV e gestão financeira em uma única plataforma.",
    primaryCtaLabel: "Conhecer a plataforma",
    arenaCtaLabel: "Sou dono de arena",
    capabilityTitle: "Gestão Inteligente",
    capabilityDescription: "Organize horários, reservas e ocupação em um só lugar",
  },
  audiences: {
    athlete: {
      title: "Atletas & Praticantes",
      description: "Para quem ama praticar esportes e busca uma forma prática de encontrar locais, reservar horários, organizar partidas entre amigos e competir.",
      ctaLabel: "Explorar Quadras e Jogos",
    },
    arenaOwner: {
      title: "Donos de Arenas & Gestores",
      description: "Para proprietários, administradores e operadores de complexos esportivos que buscam previsibilidade, organização e controle centralizado de sua infraestrutura.",
      ctaLabel: "Cadastrar Minha Arena",
    },
  },
  about: {
    title: "Quem somos",
    description: "O Hub Esportivo é uma plataforma de tecnologia voltada à organização e gestão de arenas, clubes, centros de treinamento e espaços esportivos. Desenvolvemos soluções práticas e intuitivas para transformar a rotina operacional em um processo integrado e sem atritos.",
  },
  objective: {
    title: "Um só lugar para cuidar da operação esportiva.",
    description: "O objetivo fundamental do Hub Esportivo é unificar a operação dos espaços esportivos, facilitando a rotina de quem gerencia o estabelecimento e garantindo agilidade e transparência para quem utiliza as quadras.",
  },
  modules: [
    {
      id: "courts",
      title: "1. Quadras e Arenas",
      description: "Gestão das quadras, disponibilidade, horários e configurações.",
      enabled: true,
    },
    {
      id: "bookings",
      title: "2. Reservas",
      description: "Agendamento e controle da ocupação das quadras.",
      enabled: true,
    },
    {
      id: "lessons",
      title: "3. Aulas e Treinos",
      description: "Organização de atividades esportivas e participantes.",
      enabled: true,
    },
    {
      id: "tournaments",
      title: "4. Torneios",
      description: "Gestão de torneios, inscrições, despesas e estruturas de competição.",
      enabled: true,
    },
    {
      id: "pdv",
      title: "5. Vendas e PDV",
      description: "Produtos, estoque, vendas e consumação.",
      enabled: true,
    },
    {
      id: "schedule",
      title: "6. Horários e Eventos",
      description: "Gestão de eventos, horários e atividades.",
      enabled: true,
    },
    {
      id: "finance",
      title: "7. Financeiro",
      description: "Acompanhamento financeiro e modelos comerciais.",
      enabled: true,
    },
    {
      id: "notifications",
      title: "8. Notificações",
      description: "Comunicação de eventos importantes da operação.",
      enabled: true,
    },
  ],
  commercialModels: {
    title: "Opções comerciais transparentes para cada momento da sua arena",
    description: "Escolha o modelo de parceria ou assinatura que melhor se adapta à maturidade e fluxo financeiro do seu complexo esportivo.",
    models: [
      {
        id: "SPLIT",
        title: "SPLIT",
        subtitle: "Comissão percentual sobre reservas, sem mensalidade fixa.",
        description: "Ideal para novas arenas, quadras em expansão ou gestores que buscam alinhar os custos da tecnologia ao volume real de reservas efetuadas.",
        enabled: true,
      },
      {
        id: "FIXED_MONTHLY",
        title: "MENSALIDADE FIXA",
        subtitle: "Mensalidade fixa, sem comissão sobre reservas conforme a regra comercial homologada.",
        description: "Perfeito para complexos esportivos consolidados com grande volume de agendamentos que priorizam custo mensal previsível, sem incidência de percentual sobre as reservas.",
        enabled: true,
      },
      {
        id: "HYBRID",
        title: "HÍBRIDO",
        subtitle: "Mensalidade reduzida combinada com comissão percentual sobre reservas.",
        description: "Combina uma assinatura mensal reduzida com uma taxa reduzida sobre as reservas, adaptando o investimento ao ritmo sazonal da arena.",
        enabled: true,
      },
    ],
  },
  benefits: {
    title: "Por que usar o Hub Esportivo",
    description: "Benefícios práticos fundamentados nas ferramentas e fluxos reais da plataforma, sem promessas infladas.",
    items: [
      {
        id: "centralized_operation",
        title: "Operação Centralizada",
        description: "Toda a rotina do complexo esportivo conectada em uma só interface, desde a abertura do portão até o fechamento de caixa.",
        enabled: true,
      },
      {
        id: "fewer_tools",
        title: "Menos Ferramentas Separadas",
        description: "Evite conciliar anotações manuais, cadernos físicos, planilhas desatualizadas e grupos dispersos em aplicativos de mensagem.",
        enabled: true,
      },
      {
        id: "realtime_courts",
        title: "Gestão de Quadras em Tempo Real",
        description: "Visualização imediata dos horários ocupados, livres ou bloqueados por eventos ou manutenções técnicas.",
        enabled: true,
      },
      {
        id: "bookings_subscribers",
        title: "Reservas e Mensalistas",
        description: "Administração transparente de reservas avulsas e contratos recorrentes de horários fixos semanais.",
        enabled: true,
      },
      {
        id: "integrated_pdv",
        title: "Vendas e PDV Integrado",
        description: "Controle o bar, lanchonete, aluguel de raquetes e bolas no mesmo sistema em que os jogos são agendados.",
        enabled: true,
      },
      {
        id: "tournaments_events",
        title: "Eventos e Torneios Organizados",
        description: "Criação e execução completa de campeonatos com gerenciamento de duplas, chaves e fases eliminatórias.",
        enabled: true,
      },
      {
        id: "financial_clarity",
        title: "Clareza Financeira",
        description: "Acompanhamento preciso de entradas operacionais, vendas de produtos e modelos de remuneração da arena.",
        enabled: true,
      },
      {
        id: "user_communication",
        title: "Comunicação Direta com Usuários",
        description: "Notificações instantâneas sobre confirmação de reservas, alterações de horários e comunicados da arena.",
        enabled: true,
      },
      {
        id: "multiplatform_pwa",
        title: "Acesso Multiplataforma (PWA)",
        description: "Funciona em computadores, notebooks, tablets e smartphones Android/iOS sem exigir downloads pesados.",
        enabled: true,
      },
    ],
  },
  contact: {
    title: "Dados Corporativos",
    description: "Informações cadastrais e canais oficiais de comunicação institucional.",
  },
  finalCta: {
    title: "Pronto para conhecer o Hub Esportivo?",
    subtitle: "Acesse a plataforma para explorar quadras e modalidades, ou cadastre seu espaço esportivo e transforme sua gestão.",
  },
  footer: {
    description: "Gestão completa para arenas, clubes e espaços esportivos.",
    copyright: "Todos os direitos reservados.",
  },
};
